import { useEffect, useRef, useState } from 'react';
import { traderProfiles } from '../data';

/* ------------------------------------------------------------------ */
/*  Color mapping for the dominant cost highlight                     */
/* ------------------------------------------------------------------ */
const costColorMap: Record<string, {
  accent: string;
  bg: string;
  border: string;
}> = {
  'Spread widening at rollover': {
    accent: 'text-accent-600',
    bg: 'bg-accent-50',
    border: 'border-accent-300',
  },
  'Conversion markup': {
    accent: 'text-primary-600',
    bg: 'bg-primary-50',
    border: 'border-primary-300',
  },
  'Platform & data fees': {
    accent: 'text-secondary-600',
    bg: 'bg-secondary-50',
    border: 'border-secondary-300',
  },
  'Withdrawal fees': {
    accent: 'text-secondary-600',
    bg: 'bg-secondary-50',
    border: 'border-secondary-300',
  },
  'Dividend adjustments': {
    accent: 'text-accent-600',
    bg: 'bg-accent-50',
    border: 'border-accent-300',
  },
  'Inactivity fees': {
    accent: 'text-primary-600',
    bg: 'bg-primary-50',
    border: 'border-primary-300',
  },
  'Other': {
    accent: 'text-foreground-600',
    bg: 'bg-background-100',
    border: 'border-background-300',
  },
};

/* ------------------------------------------------------------------ */
/*  Count-up hook                                                       */
/* ------------------------------------------------------------------ */
function useCountUp(target: string, active: boolean, delay: number) {
  const [display, setDisplay] = useState('—');
  useEffect(() => {
    if (!active) { setDisplay('—'); return; }
    const timer = setTimeout(() => {
      // Extract numeric parts from ranges like "$1,800–$3,200"
      const parts = target.match(/[\d,]+/g);
      if (!parts || parts.length < 2) { setDisplay(target); return; }
      const low  = parseInt(parts[0].replace(/,/g, ''), 10);
      const high = parseInt(parts[1].replace(/,/g, ''), 10);
      let frame = 0;
      const totalFrames = 50;
      const tick = setInterval(() => {
        frame++;
        const p = frame / totalFrames;
        const ease = 1 - Math.pow(1 - p, 3);
        const curLow  = Math.round(low  * ease);
        const curHigh = Math.round(high * ease);
        setDisplay(`$${curLow.toLocaleString()}–$${curHigh.toLocaleString()}`);
        if (frame >= totalFrames) clearInterval(tick);
      }, 24);
      return () => clearInterval(tick);
    }, delay);
    return () => clearTimeout(timer);
  }, [target, active, delay]);
  return display;
}

/* ------------------------------------------------------------------ */
/*  Dotted leader line component                                       */
/* ------------------------------------------------------------------ */
function DottedLeader() {
  return (
    <div className="flex-1 mx-3 flex items-end">
      <div className="w-full border-b border-dotted border-background-300" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Individual receipt card                                            */
/* ------------------------------------------------------------------ */
interface ProfileData {
  name: string;
  subtitle: string;
  tradesPerDay: number;
  positionMinutes?: string;
  positionDays?: string;
  dominantCosts: Array<{ cost: string; percent: number }>;
  annualEstimate: string;
  insight: string;
}

function ReceiptCard({
  profile,
  pIdx,
  inView,
}: {
  profile: ProfileData;
  pIdx: number;
  inView: boolean;
}) {
  const topCost = profile.dominantCosts[0];
  const topStyle = costColorMap[topCost.cost] || costColorMap['Other'];
  const annualDisplay = useCountUp(
    profile.annualEstimate,
    inView,
    pIdx * 150 + 600,
  );

  return (
    <article
      key={profile.name}
      className="relative bg-background-50 border border-background-200/80 flex flex-col h-full"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(24px)',
        transition: 'opacity 650ms ease-out, transform 650ms ease-out',
        transitionDelay: `${pIdx * 140}ms`,
      }}
    >
      {/* Receipt serrated top edge */}
      <div className="absolute -top-[6px] left-0 right-0 flex overflow-hidden h-[6px]">
        {Array.from({ length: 60 }).map((_, i) => (
          <div
            key={i}
            className="shrink-0 w-[10px] h-[10px] rotate-45 border-r border-b border-background-200/80 -ml-[3px]"
            style={{ background: 'inherit' }}
          />
        ))}
      </div>

      {/* ── Header ── */}
      <div className="px-6 pt-8 pb-5">
        {/* Metric pills — actual trading style data, not decorative labels */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex flex-col items-center min-w-[72px]">
            <span className="text-[13px] font-[800] text-foreground-950 tabular-nums leading-none">
              {profile.tradesPerDay >= 1 ? profile.tradesPerDay : `<1`}
            </span>
            <span className="text-[9px] font-bold text-foreground-400 uppercase tracking-[0.12em] mt-1">
              Trades / day
            </span>
          </div>
          <div className="w-px h-6 bg-background-200" />
          <div className="flex flex-col items-center min-w-[72px]">
            <span className="text-[13px] font-[800] text-foreground-950 tabular-nums leading-none">
              {profile.positionMinutes || profile.positionDays}
            </span>
            <span className="text-[9px] font-bold text-foreground-400 uppercase tracking-[0.12em] mt-1">
              Avg hold
            </span>
          </div>
          <div className="w-px h-6 bg-background-200" />
          <div className="flex flex-col items-center min-w-[72px]">
            <span className="text-[13px] font-[800] text-foreground-950 tabular-nums leading-none">
              {profile.tradesPerDay >= 10 ? `High` : profile.tradesPerDay >= 3 ? `Med` : `Low`}
            </span>
            <span className="text-[9px] font-bold text-foreground-400 uppercase tracking-[0.12em] mt-1">
              Frequency
            </span>
          </div>
        </div>

        <div className="h-px bg-background-200 mb-5" />
        <div className="text-[24px] font-[800] text-foreground-950 tracking-tight leading-none mb-1">
          {profile.name}
        </div>
        <div className="text-[13px] text-foreground-500">
          {profile.subtitle}
        </div>
      </div>

      {/* ── Cost line items ── */}
      <div className="px-6 pb-2">
        {profile.dominantCosts.map((item, iIdx) => {
          const isTop = iIdx === 0;
          const style = costColorMap[item.cost] || costColorMap['Other'];
          return (
            <div
              key={item.cost}
              className={`flex items-baseline py-2.5 -mx-2 px-2 rounded-sm ${
                isTop ? `${style.bg} ${style.border} border-l-2` : ''
              }`}
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateX(0)' : 'translateX(-10px)',
                transition: 'opacity 500ms ease-out, transform 500ms ease-out',
                transitionDelay: `${pIdx * 140 + 350 + iIdx * 90}ms`,
              }}
            >
              <div className="shrink-0 min-w-0">
                <div className={`text-[13px] font-semibold ${isTop ? 'text-foreground-950' : 'text-foreground-600'}`}>
                  {item.cost}
                </div>
                {isTop && (
                  <div className="text-[9px] font-bold uppercase tracking-[0.15em] mt-0.5" style={{ color: 'oklch(var(--accent-500))' }}>
                    Primary drain
                  </div>
                )}
              </div>
              <DottedLeader />
              <div className="shrink-0 text-right">
                <span className={`text-[18px] font-[800] tabular-nums tracking-tight ${isTop ? style.accent : 'text-foreground-600'}`}>
                  {item.percent}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Double line before total ── */}
      <div className="px-6 pt-2 pb-4">
        <div className="border-t-2 border-foreground-900" />
        <div className="border-t border-background-200 mt-0.5" />
      </div>

      {/* ── Total ── */}
      <div className="px-6 pb-1 pt-2">
        <span className="block text-[11px] font-semibold text-foreground-500 uppercase tracking-wider leading-none mb-3">
          Total hidden cost
        </span>
        <span className="block text-right text-[34px] md:text-[38px] font-[900] text-foreground-950 tabular-nums tracking-tight leading-none">
          {annualDisplay}
        </span>
      </div>

      {/* ── Insight callout ── */}
      <div className="px-6 pt-5 pb-8 flex-1 flex flex-col">
        <div
          className="border-l-2 pl-4 py-3"
          style={{
            borderColor: 'oklch(var(--accent-500))',
            background: 'oklch(var(--accent-50))',
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(8px)',
            transition: 'opacity 600ms ease-out, transform 600ms ease-out',
            transitionDelay: `${pIdx * 140 + 900}ms`,
          }}
        >
          <p className="text-[12px] text-foreground-700 leading-[1.6] italic">
            {profile.insight}
          </p>
        </div>
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ */
/*  Main section                                                       */
/* ------------------------------------------------------------------ */
export default function TraderProfileImpact() {
  const [inView, setInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="w-full bg-background-50 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">

        {/* ── Section header ─────────────────────────────────────── */}
        <div className="mb-10 md:mb-14">
          <h2 className="text-[32px] md:text-[44px] font-[750] leading-[1.1] text-foreground-950 tracking-tight mb-5 max-w-[680px]">
            The same market.
            <br className="hidden md:block" /> Three very different bills.
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600 max-w-[560px]">
            Each trading style pays a different set of hidden costs. Find your profile — the biggest number is the one you need to fix first.
          </p>
        </div>

        {/* ── Three statements ───────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {traderProfiles.map((profile, pIdx) => (
            <ReceiptCard
              key={profile.name}
              profile={profile as ProfileData}
              pIdx={pIdx}
              inView={inView}
            />
          ))}
        </div>

      </div>
    </section>
  );
}